import { Auth } from "aws-amplify";
import router from "@/router";
import { client } from "@/main";
import { premiumQuery } from "@/graphql/query";
import { format } from "date-fns";

export default {
  async login({ commit, dispatch, state }, { email, password }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const { attributes } = await Auth.signIn(email, password);
      commit("setEmail", attributes.email);
      if (attributes["custom:firstname"]) {
        const fullName =
          attributes["custom:firstname"] + " " + attributes["custom:lastname"];
        commit("setFullName", fullName);
      }
      dispatch("checkPremiumStatus");
      dispatch("projectStore/getAvailableStandards", null, { root: true });
      window.fastspring.builder.recognize({
        email: state.email,
        firstName: state.firstname,
        lastName: state.lastname,
      });
      const redirectUrl = router.currentRoute.query.redirect;
      router.push(redirectUrl || "/");
    } catch (error) {
      commit("setEmail", email);
      commit("setError", error);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async logout({ commit }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      await Auth.signOut();
      commit("setEmail", "");
      commit("setFullName", "");
      commit("setFirstname", "");
      commit("setLastname", "");
      commit("setFastspringAccountId", null);
      commit("setActiveSubscription", null);
      commit("setAccountManagementURL", null);
      router.push("/login");
    } catch (error) {
      console.error(`Error signing out: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async getUserDetails({ commit }) {
    try {
      const {
        idToken: { payload },
      } = await Auth.currentSession();
      if (payload["custom:firstname"] !== undefined) {
        const fullName =
          payload["custom:firstname"] + " " + payload["custom:lastname"];
        commit("setFirstname", payload["custom:firstname"]);
        commit("setLastname", payload["custom:lastname"]);
        commit("setFullName", fullName);
      }
      commit("setFastspringAccountId", payload["custom:fastspring-accountid"]);
      commit("setEmail", payload.email);
      commit("setShowGuidanceTooltip", !payload["custom:guidance_seen"]);
      commit("setNewsLastSeen", payload["custom:news_last_seen_date"]);
    } catch (error) {
      console.error(`Error getting personal info: ${error}`);
    }
  },
  async signup({ commit }, { firstname, lastname, email, password }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const { userConfirmed } = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          "custom:firstname": firstname,
          "custom:lastname": lastname,
          "custom:disclaimer_accepted": new Date().toString(),
        },
      });
      commit("setSignupDone", true);
      commit("setUserConfirmed", userConfirmed);
    } catch (error) {
      commit("setError", error);
      console.error(`Error signing up: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async resendConfirmationCode({ commit }, username) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      await Auth.resendSignUp(username);
    } catch (error) {
      commit("setError", error);
      console.error(`Error sending confirmation code: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async confirmSignup({ commit }, { username, code }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      await Auth.confirmSignUp(username, code);
      commit("setUserConfirmed", true);
    } catch (error) {
      commit("setError", error);
      console.error(`Error confirming sign up: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async forgotPassword({ commit }, { username }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      await Auth.forgotPassword(username);
      commit("setShowEmailSent", true);
      commit("setShowForgotPassword", false);
      commit("setEmail", username);
    } catch (error) {
      commit("setError", error);
      console.error(
        `Error sending confirmation code for forgot password: ${error}`
      );
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async forgotPasswordSubmit({ state, commit }, { code, new_password }) {
    const { email: username } = state;
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      await Auth.forgotPasswordSubmit(username, code, new_password);
      commit("setShowEmailSent", false);
    } catch (error) {
      commit("setError", error);
      console.error(`Error sending new password: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async setGuidanceAttribute({ commit }) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        "custom:guidance_seen": "true",
      });
      commit("setShowGuidanceTooltip", false);
    } catch (error) {
      console.error(`Error setting guidance attribute: ${error}`);
    }
  },
  async setFastspringAccountIdAttribute(
    { dispatch },
    { accountId, recheckStatus }
  ) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        "custom:fastspring-accountid": accountId,
      });
      recheckStatus && dispatch("checkPremiumStatus");
    } catch (error) {
      console.error(`Error setting fastspring account id attribute: ${error}`);
    }
  },
  async checkPremiumStatus({ commit, dispatch }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const { data } = await client.query({
        query: premiumQuery,
        fetchPolicy: "network-only",
      });
      if (data.premium.account_id && data.premium.update_user_attributes) {
        dispatch("setFastspringAccountIdAttribute", {
          accountId: data.premium.account_id,
          recheckStatus: false,
        });
      }
      data.premium.management_url &&
        commit("setAccountManagementURL", data.premium.management_url);
      data.premium.account_id &&
        commit("setFastspringAccountId", data.premium.account_id);
      commit("setActiveSubscription", data.premium.active_subscription);
      commit("loaderStore/setIsLoading", false, { root: true });
    } catch (error) {
      commit("loaderStore/setIsLoading", false, { root: true });
      console.error(
        `Error getting status about an active subscription: ${error}`
      );
    }
  },
  async setNewsLastSeenAttribute({ commit }) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const newsSeenDate = format(new Date(), "dd.MM.yyyy");
      await Auth.updateUserAttributes(user, {
        "custom:news_last_seen_date": newsSeenDate,
      });
      commit("setNewsLastSeen", newsSeenDate);
    } catch (error) {
      console.error(
        `Error setting news last seen attribute for user: ${error}`
      );
    }
  },
};
